import { Policy, SessionPolicies } from "@cartridge/presets";
import {
  AddInvokeTransactionResult,
  ChainId,
  Signature,
  TypedData,
} from "@starknet-io/types-js";
import {
  Abi,
  BigNumberish,
  Call,
  constants,
  InvocationsDetails,
} from "starknet";
import { KeychainIFrame } from "./iframe";
import {
  AUTH_EXTERNAL_WALLETS,
  EXTERNAL_WALLETS,
  ExternalWallet,
  ExternalWalletResponse,
  ExternalWalletType,
} from "./wallets/types";

export type KeychainSession = {
  chainId: constants.StarknetChainId;
  policies: Policy[];
  maxFee: BigNumberish;
  expiresAt: bigint;
  credentials: {
    authorization: string[];
    privateKey: string;
  };
};

export const EMBEDDED_WALLETS = [
  "google",
  "webauthn",
  "discord",
  "walletconnect",
  "password",
  "sms",
] as const;

export type EmbeddedWallet = (typeof EMBEDDED_WALLETS)[number];

export const ALL_AUTH_OPTIONS = [
  ...EMBEDDED_WALLETS,
  ...EXTERNAL_WALLETS,
] as const;

export type AuthOption = (typeof ALL_AUTH_OPTIONS)[number];

export const IMPLEMENTED_AUTH_OPTIONS = [
  ...EMBEDDED_WALLETS,
  ...AUTH_EXTERNAL_WALLETS,
];

export type AuthOptions = (typeof IMPLEMENTED_AUTH_OPTIONS)[number][];

export enum ResponseCodes {
  SUCCESS = "SUCCESS",
  NOT_CONNECTED = "NOT_CONNECTED",
  ERROR = "ERROR",
  CANCELED = "CANCELED",
  USER_INTERACTION_REQUIRED = "USER_INTERACTION_REQUIRED",
}

export type ConnectError = {
  code: ResponseCodes;
  message: string;
  error?: ControllerError;
};

export type ControllerError = {
  code: Number;
  message: string;
  data?: any;
};

export type ConnectReply = {
  code: ResponseCodes.SUCCESS;
  address: string;
  policies?: SessionPolicies;
  keepOpen?: boolean;
};

export type ExecuteReply =
  | (AddInvokeTransactionResult & {
      code: ResponseCodes.SUCCESS;
    })
  | {
      code: ResponseCodes.USER_INTERACTION_REQUIRED;
    };

export type ProbeReply = {
  code: ResponseCodes.SUCCESS;
  address: string;
  rpcUrl?: string;
};

export type DeployReply = {
  code: ResponseCodes.SUCCESS;
  transaction_hash: string;
};

export type LocationCoordinates = {
  latitude: number;
  longitude: number;
  accuracy: number;
  altitude?: number | null;
  altitudeAccuracy?: number | null;
  heading?: number | null;
  speed?: number | null;
  timestamp: number;
};

export type LocationPromptReply = {
  code: ResponseCodes.SUCCESS;
  location: LocationCoordinates;
};

export type LocationGateOptions = {
  /** Locations to allow. Country codes ("US") or region codes ("US-CA", "DE-BY"). */
  allowed?: string[];
  /** Locations to block. Country codes ("SG") or region codes ("US-HI"). Blocked wins over allowed. */
  blocked?: string[];
};

export type AgeGateOptions = {
  /** Minimum age required to play. Identity verification enforces legal age. @default 18 */
  minimumAge: number;
};

export type IFrames = {
  keychain?: KeychainIFrame;
  version?: number;
};

export interface LookupRequest {
  usernames?: string[];
  addresses?: string[];
}

export interface LookupResult {
  username: string;
  addresses: string[];
}

export interface LookupResponse {
  results: LookupResult[];
}

export interface HeadlessUsernameLookupResult {
  username: string;
  exists: boolean;
  signers: AuthOption[];
}

export enum FeeSource {
  PAYMASTER = "PAYMASTER",
  CREDITS = "CREDITS",
}

type ContractAddress = string;
type CartridgeID = string;
export type ControllerAccounts = Record<ContractAddress, CartridgeID>;

export interface Keychain {
  probe(rpcUrl: string): Promise<ProbeReply | ConnectError>;
  connect(options?: ConnectOptions): Promise<ConnectReply | ConnectError>;
  disconnect(): void;

  reset(): void;
  revoke(origin: string): void;

  deploy(): Promise<DeployReply | ConnectError>;
  execute(
    calls: Call | Call[],
    abis?: Abi[],
    transactionsDetail?: InvocationsDetails,
    sync?: boolean,
    feeSource?: any,
    error?: ControllerError,
  ): Promise<ExecuteReply | ConnectError>;
  signMessage(
    typedData: TypedData,
    account: string,
    async?: boolean,
  ): Promise<Signature | ConnectError>;
  updateSession(
    policies?: SessionPolicies,
    preset?: string,
  ): Promise<ConnectReply | ConnectError>;
  openSettings(): Promise<void | ConnectError>;
  session(): Promise<KeychainSession>;
  sessions(): Promise<{
    [key: string]: KeychainSession;
  }>;
  delegateAccount(): string;
  username(): string;
  credits(): Promise<number>;
  openPurchaseCredits(): void;
  openExecute(calls: Call[]): Promise<void>;
  openLocationPrompt(
    options?: LocationPromptOptions,
  ): Promise<LocationPromptReply | ConnectError>;
  switchChain(rpcUrl: string): Promise<void>;
  openBundle(
    id: number,
    registryAddress: string,
    options?: BundleOptions,
  ): Promise<void>;
  openStarterPack(
    id: string | number,
    options?: StarterpackOptions,
  ): Promise<void>;
  openMerkleDrops(keys: string[], options?: MerkleDropsOptions): Promise<void>;
  navigate(path: string): Promise<void>;

  // External wallet methods
  externalDetectWallets(): Promise<ExternalWallet[]>;
  externalConnectWallet(
    type: ExternalWalletType,
    address?: string,
  ): Promise<ExternalWalletResponse>;
  externalSignMessage(
    type: ExternalWalletType,
    message: string,
  ): Promise<ExternalWalletResponse>;
  externalSignTypedData(
    type: ExternalWalletType,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: any,
  ): Promise<ExternalWalletResponse>;
  externalGetBalance(
    type: ExternalWalletType,
    tokenAddress?: string,
  ): Promise<ExternalWalletResponse>;
  externalSwitchChain(
    type: ExternalWalletType,
    chainId: string,
  ): Promise<boolean>;
}

export interface Profile {
  navigate(path: string): void;
  switchChain(rpcUrl: string): Promise<void>;
}

export interface Modal {
  open: () => void;
  close: () => void;
}

/**
 * Options for configuring the controller
 */
export type ControllerOptions = ProviderOptions & KeychainOptions;

export type IFrameOptions = {
  /** The ID of the starter pack to use */
  starterPackId?: string;
  /** The preset to use */
  preset?: string;
};

/**
 * A chain covered by a multichain session approval, resolved to its rpcUrl
 * SDK-side so the keychain never re-derives chain ids from URLs.
 */
export type SessionChain = {
  chainId: string;
  rpcUrl: string;
};

export type Chain = {
  /** RPC url */
  rpcUrl: string;
  /** Chain id (for ui only) */
  chainId?: ChainId;
  /** Chain name (for ui only) */
  name?: string;
  /** Chain icon url (for ui only) */
  icon?: string;
};

export type ProviderOptions = {
  defaultChainId?: ChainId;
  chains?: Chain[];
};

export type DefaultPaymentMethod = "credit-card";

export type KeychainOptions = IFrameOptions & {
  policies?: SessionPolicies;
  /** The URL of keychain */
  url?: string;
  /** The origin of keychain */
  origin?: string;
  /** The RPC URL to use (derived from defaultChainId) */
  rpcUrl?: string;
  /** Propagate transaction errors back to caller instead of showing modal */
  propagateSessionErrors?: boolean;
  /** How to display transaction/execution errors to the user ('modal' | 'notification' | 'silent'). Defaults to 'modal'. */
  errorDisplayMode?: "modal" | "notification" | "silent";
  /** The fee source to use for execute from outside */
  feeSource?: FeeSource;
  /** Signup options (the order of the options is reflected in the UI. It's recommended to group socials and wallets together ) */
  signupOptions?: AuthOptions;
  /** Preferred fallback when no explicit choice or funded Controller token is available. */
  defaultPaymentMethod?: DefaultPaymentMethod;
  /** Use Coinflow's sandbox checkout, including on mainnet. Sandbox payments do not grant spendable credits. */
  coinflowSandbox?: boolean;
  /** When true, manually provided policies will override preset policies. Default is false. */
  shouldOverridePresetPolicies?: boolean;
  /**
   * Explicit opt-in to multichain sessions: chain IDs covered by a single
   * session approval flow. Each entry must correspond to a chain configured
   * via `chains` (or a default Cartridge chain), and `policies` or `preset`
   * must be provided. When absent, sessions are created for the active chain
   * only (current behavior).
   */
  multichainSessions?: ChainId[];
  /** The project name of Slot instance. */
  slot?: string;
  /** The Torii indexer URL used to fetch tokens/collections. Takes precedence over `slot`. */
  toriiUrl?: string;
  /** The namespace to use to fetch trophies data from indexer. Will be mandatory once profile page is in production */
  namespace?: string;
  /** The tokens to be listed on Inventory modal */
  tokens?: Tokens;
  /** When true, defer iframe mounting until connect() is called. Reduces initial load and resource fetching. */
  lazyload?: boolean;
  /** When true, force WebAuthn operations to run in a popup window instead of the iframe. Useful for development and testing. */
  webauthnPopup?: boolean;
};

export type ProfileContextTypeVariant =
  | "inventory"
  | "trophies"
  | "achievements"
  | "quests"
  | "leaderboard"
  | "activity";

export type Token = "eth" | "strk" | "lords" | "usdc" | "usdt";

export type Tokens = {
  erc20?: Token[];
};

export type OpenOptions = {
  /** The URL to redirect to after authentication (defaults to current page) */
  redirectUrl?: string;
};

export type LocationPromptOptions = {
  /** Optional path to navigate to after completion (standalone mode). */
  returnTo?: string;
};

export type SocialClaimOptions = {
  shareMessage: string;
};

export type BundleOptions = {
  /** Callback fired after the Play button closes the starterpack modal */
  onPurchaseComplete?: () => void;
  /** Options for social claim conditional starterpack */
  socialClaimOptions?: SocialClaimOptions;
  /** When true, hides the quantity selector and fixes the purchase to a single bundle */
  singlePurchaseOnly?: boolean;
};

export type StarterpackOptions = {
  /** The preimage to use */
  preimage?: string;
  /** Callback fired after the Play button closes the starterpack modal */
  onPurchaseComplete?: () => void;
  /** When true, hides the quantity selector and fixes the purchase to a single starterpack */
  singlePurchaseOnly?: boolean;
};

export type MerkleDropsOptions = {
  /** Optional title to show for the grouped merkle-drop claim */
  title?: string;
  /** Optional description to show for the grouped merkle-drop claim */
  description?: string;
  /** The preimage to use */
  preimage?: string;
  /** Callback fired after the Play button closes the claim modal */
  onClaimComplete?: () => void;
};

// Connect options (used by controller.connect)
export interface ConnectOptions {
  /** Signup options (shown in UI when not headless) */
  signupOptions?: AuthOptions;
  /** Headless mode username (when combined with signer) */
  username?: string;
  /** Headless mode signer option (auth method) */
  signer?: AuthOption;
  /** Required when signer is "password" */
  password?: string;
}

/** Options for updating session policies at runtime */
export type UpdateSessionOptions = {
  /** Session policies to set */
  policies?: SessionPolicies;
  /** Preset name to resolve policies from */
  preset?: string;
};

export type HeadlessConnectOptions = Required<
  Pick<ConnectOptions, "username" | "signer">
> &
  Pick<ConnectOptions, "password">;

export type HeadlessConnectReply =
  | {
      code: ResponseCodes.SUCCESS;
      address: string;
    }
  | {
      code: ResponseCodes.USER_INTERACTION_REQUIRED;
      requestId: string;
      message?: string;
    }
  | ConnectError;
